import React, { useEffect, useId, useRef } from 'react';
import Icon from '../shared/Icon';
import { useEscapeKey } from '../shared/useEscapeKey';
import './Library.css'; // library-sr-only, used inside dialogs
import './LibraryDialog.css';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="file"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * The Library's dialog shell (PRD-1606, PRD-1607): one backdrop holds whichever
 * dialog is open. Focus moves in to `initialFocus`, Tab is trapped, and Escape,
 * the close button or the backdrop all call `onClose`. On close, focus goes
 * back to `opener`, the control that opened it, or to `fallbackFocus` if that
 * control is gone by then (the item it acted on left the view).
 *
 * `opener` is passed in rather than read from document.activeElement: Safari
 * doesn't focus a button on click, so activeElement would be the body.
 *
 * `children` is the body; `title` is the heading the dialog is named by. With
 * no `initialFocus`, focus goes to the close button. `className` adds to the
 * dialog's own, for a dialog that needs another size. `covered` is true while
 * another dialog sits on top of this one, which then owns Escape.
 */
function LibraryDialog({ title, size, className, covered = false, initialFocus, opener, fallbackFocus, onClose, children }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEscapeKey(!covered, onClose);

  useEffect(() => {
    const returnTo = opener || document.activeElement;
    const first = initialFocus?.current || closeRef.current;
    if (first) first.focus();
    return () => {
      // Read at close on purpose: the fallback is whatever is on the page then.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const target = returnTo && returnTo.isConnected ? returnTo : fallbackFocus?.current;
      if (target && target.focus) target.focus();
    };
  }, [opener, fallbackFocus, initialFocus]);

  const trapTab = (e) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return (
    // A file dropped anywhere outside a drop zone would otherwise be opened by
    // the browser, navigating away from the console and losing the draft.
    <div
      className="library-scrim"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <div
        ref={dialogRef}
        className={['library-dialog', size ? `library-dialog--${size}` : '', className].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={trapTab}
      >
        <div className="library-dialog-head">
          <h2 className="library-dialog-title" id={titleId}>{title}</h2>
          <button ref={closeRef} type="button" className="library-close-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default LibraryDialog;
